import { motion } from 'framer-motion';
import { Gauge, Siren, Radar } from 'lucide-react';

const LaggingIndicatorSection = () => {
  const problems = [
    {
      icon: Gauge,
      number: '01',
      title: 'The dashboard is not live',
      description: 'Your Amazon Account Health dashboard isn\'t real-time. It shows you data that\'s already hours or days old — and that delay can cost you everything.',
      color: 'red'
    },
    {
      icon: Siren,
      number: '02',
      title: 'Warning signals come first',
      description: 'There are warning signals that show up before Amazon takes action on your account. But you\'ll never see them in Seller Central.',
      color: 'red'
    },
    {
      icon: Radar,
      number: '03',
      title: 'By the time you see it, it\'s too late',
      description: 'Your Account Health Rating shows problems after they\'ve already happened. By then, Amazon has already flagged your account.',
      color: 'red'
    }
  ];

  return (
    <section className="bg-slate-50 py-20 lg:py-24 border-y border-slate-100" data-testid="lagging-indicator-section">
      <div className="max-w-5xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-red-500">
            The Problem
          </p>
          <h2 className="font-display text-slate-800 text-3xl sm:text-4xl lg:text-5xl mt-3">
            Why Most Sellers Get Caught Off Guard
          </h2>
          <p className="text-slate-500 text-lg mt-4 max-w-2xl mx-auto">
            Amazon's Account Health dashboard was designed to show you problems — not prevent them.
          </p>
        </motion.div>

        {/* Problem Cards */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {problems.map((problem, index) => (
            <motion.article
              key={problem.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="h-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="flex items-center justify-between">
                <span className="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
                  <problem.icon className="w-6 h-6 text-red-500" strokeWidth={1.75} />
                </span>
                <span className="font-display text-3xl text-slate-200">{problem.number}</span>
              </div>
              <h3 className="font-display text-slate-800 text-lg mt-5">
                {problem.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mt-2.5">
                {problem.description}
              </p>
            </motion.article>
          ))}
        </div>

        {/* Callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-block bg-white rounded-2xl border border-purple-200 shadow-sm px-8 py-6">
            <p className="text-slate-600">
              <span className="font-semibold text-purple-600">In this free workshop</span>, you'll learn exactly what Amazon sees — and how to see it before they act.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LaggingIndicatorSection;
